import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import type { LegalSection } from "@/mocks/legalData";

interface LegalPageProps {
  breadcrumb: string;
  badge: string;
  title: string;
  updated: string;
  sections: LegalSection[];
}

export default function LegalPage({ breadcrumb, badge, title, updated, sections }: LegalPageProps) {
  return (
    <PageLayout>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: breadcrumb }]} />

        {/* Header */}
        <section className="py-16 md:py-20 bg-hero-gradient relative overflow-hidden">
          <div className="container-astro relative z-10">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
                <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">{badge}</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 mb-4 font-heading tracking-tight">
                {title}
              </h1>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <p className="text-sm text-background-400">Last updated: {updated}</p>
            </AnimatedSection>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
        </section>

        {/* Content */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="container-astro max-w-3xl">
            {sections.map((section, i) => (
              <AnimatedSection key={section.title} delay={i * 60}>
                <div className={`${i > 0 ? "mt-10" : ""}`}>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-heading tracking-tight">
                    {i + 1}. {section.title}
                  </h2>
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="text-[15px] text-foreground-600 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="container-astro max-w-3xl">
            <div className="rounded-2xl bg-background-100 border border-background-200/60 p-8 md:p-10 text-center">
              <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-2 font-heading">
                Questions about this document?
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                Reach out to our team and we'll be happy to help.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="/contact-us"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/20 whitespace-nowrap"
                >
                  Contact Us
                  <i className="ri-arrow-right-line text-xs" />
                </a>
                <a
                  href="mailto:info@astrojal.com"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#2563EB]/20 text-[#2563EB] text-sm font-semibold rounded-xl hover:bg-[#2563EB]/5 transition-all duration-300 whitespace-nowrap"
                >
                  Email Legal Team
                  <i className="ri-mail-line text-xs" />
                </a>
              </div>
            </div>
          </div>
        </section>
    </PageLayout>
  );
}